import {
  Model_AttributeDetailsGet,
  Model_AttributeValueGet,
  Model_ConfigurationAttributeGet,
  Model_ConfigurationsGet_SystemProductConfigurationItem,
  Model_LabelAndReferenceGet,
  Shared_DimensionParts
} from "src/backend/openapi/v1";
import { ChipItem } from "src/components/chip-list/chip-list-types";
import { VIEW_SETTINGS_PRODUCT_LABELS_ITEM_ID, VIEW_SETTINGS_PRODUCT_TYPE_ITEM_ID } from "src/pages/configurator/components/view-settings/view-settings-store";
import { configuratorPageK } from "src/translation-hooks/configurator-page";
import type { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import type { TSortField, TConfigurationSortField, TGroupField } from "./configurator-types";

export const TABS = {
  CONFIGURATIONS: { label: configuratorPageK.configurations, value: "CONFIGURATIONS" },
  PRODUCTS: { label: configuratorPageK.products, value: "PRODUCTS" }
} as const;

// Matches the standard hyphenated GUID format the real backend's attribute ids are always
// serialized as. Filtering by shape (rather than blacklisting known frontend sentinel strings by
// name) is deliberate: a name blacklist silently stops working the moment a sentinel is renamed
// while old values are already sitting in a user's persisted View Settings — GUID-shape validation
// excludes every frontend-only id, current or stale, without needing to track sentinel names here.
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const getSelectedAttributeIds = (selectedItemIds: string[]): string[] => selectedItemIds.filter(id => GUID_PATTERN.test(id));

export const FILTER_WIDTH = { OPEN: 450, CLOSE: 40 };

type TConfiguratorPageT = ReturnType<typeof useConfiguratorPageT>;

// Single source of truth for "sort field value -> its display label", shared by the Sort options
// Select and the rail's collapsed-label summary so they can never drift out of sync
export const getSortFieldLabel = (t: TConfiguratorPageT, field: TSortField): string => {
  const labelsByField: Record<TSortField, string> = {
    // "none" reuses the same shared label already registered for the Configurations tab
    none: t.sortByNone,
    productDescription: t.sortByProductName,
    productNumber: t.sortByProductNumber,
    productTypeName: t.sortByProductType,
    configurationName: t.sortByConfigurationName,
    createdOn: t.sortByCreatedOn,
    modifiedOn: t.sortByModifiedOn
  };
  return labelsByField[field];
};

// Same purpose as getSortFieldLabel, scoped to the Configurations tab's much smaller sort field set
export const getConfigurationSortFieldLabel = (t: TConfiguratorPageT, field: TConfigurationSortField): string => {
  const labelsByField: Record<TConfigurationSortField, string> = {
    none: t.sortByNone,
    configurationName: t.sortByConfigurationName,
    productCount: t.sortByProductCount
  };
  return labelsByField[field];
};

// Same purpose as getSortFieldLabel, for the Group by Select; null means "no grouping"
export const getGroupFieldLabel = (t: TConfiguratorPageT, field: TGroupField): string => {
  if (!field) return t.groupByNone;
  const labelsByField: Record<NonNullable<TGroupField>, string> = {
    label: t.groupByLabel,
    configurationLabel: t.groupByConfigurationLabel,
    productName: t.groupByProductName,
    productType: t.groupByProductType,
    configurationName: t.groupByConfigurationName,
    missingInfo: t.groupByMissingInfo,
    invalidInfo: t.groupByInvalidInfo,
    date: t.groupByDate
  };
  return labelsByField[field];
};

export const PRODUCT_INFO_DIALOG_WIDTH = 400;

// Accepts both the single-config model (systemProductConfigurationItemId) and the
// list-view model (systemProductConfigurationId) — whichever field is present is used as the node key.
type HierarchyItem = {
  systemProductConfigurationItemId?: string;
  systemProductConfigurationId?: string;
  parentProjectLineItemId?: string | null;
};

// A tree node wrapping the original item with its resolved children — replaces the old flat-list shape.
export type HierarchyNode<T> = T & { children: HierarchyNode<T>[] };

export const getSystemProductConfigurationItemsHierarchy = <T extends HierarchyItem>(systemProductConfigurationItems: T[]): HierarchyNode<T>[] => {
  const getItemId = (item: HierarchyItem): string => item.systemProductConfigurationItemId ?? item.systemProductConfigurationId ?? "";

  if (!systemProductConfigurationItems?.length) return [];

  // Group items by their parent id so each node's children can be looked up in O(1).
  const childrenByParentId = new Map<string, T[]>();
  systemProductConfigurationItems.forEach(item => {
    const parentId = item.parentProjectLineItemId;
    if (parentId === null || parentId === undefined) return;
    // Read back the just-set/just-existing bucket instead of asserting non-null on the Map lookup
    const bucket = childrenByParentId.get(parentId) ?? [];
    bucket.push(item);
    childrenByParentId.set(parentId, bucket);
  });

  // visited guards against cycles by tracking ids on the current descent path.
  function buildNode(item: T, visited: Set<string>): HierarchyNode<T> {
    const id = getItemId(item);
    const nextVisited = new Set(visited).add(id);
    const childItems = childrenByParentId.get(id) ?? [];
    const children = childItems.filter(child => !nextVisited.has(getItemId(child))).map(child => buildNode(child, nextVisited));
    return { ...item, children };
  }

  // Only true roots (parentProjectLineItemId === null) start a tree; unreachable/cyclic items are dropped.
  const roots = systemProductConfigurationItems.filter(item => item.parentProjectLineItemId === null);
  return roots.map(root => buildNode(root, new Set()));
};

// Walks parentProjectLineItemId up from itemId to the tree's root (parentProjectLineItemId === null).
// Used by SharedAttribute rendering, which must always read/write the root item regardless of which
// item in the tree is currently selected.
export const findRootAncestor = <T extends HierarchyItem>(items: T[], itemId: string): T | undefined => {
  const getItemId = (item: HierarchyItem): string => item.systemProductConfigurationItemId ?? item.systemProductConfigurationId ?? "";

  const itemsById = new Map<string, T>();
  items.forEach(item => itemsById.set(getItemId(item), item));

  const startItem = itemsById.get(itemId);
  if (!startItem) return undefined;

  // visited guards against cycles/dangling parent ids so an unresolvable chain falls back to
  // startItem instead of looping forever or throwing.
  const visited = new Set<string>([itemId]);
  let current = startItem;
  while (current.parentProjectLineItemId !== null && current.parentProjectLineItemId !== undefined) {
    const parent = itemsById.get(current.parentProjectLineItemId);
    if (!parent || visited.has(getItemId(parent))) return startItem;
    visited.add(getItemId(parent));
    current = parent;
  }
  return current;
};

// How deep an item sits in its configuration: 1 for the item directly under it, 2 for that item's
// children, and so on. The backend resolves it from the system package's line hierarchy, so — unlike
// reconstructing the tree from parentProjectLineItemId — it stays right when an attribute filter prunes
// an item's ancestors out of the response (that filter runs server-side, per line item).
// 0 is not a level: it means the package chain could not place the item, and is treated as top-level
// here, which is how such an item behaved before depth existed.
type DepthItem = { depth?: number };

// Labels belong at the top of a configuration: on the item directly under it, or one level below that.
export const canItemHaveLabels = (item: DepthItem): boolean => (item.depth ?? 0) <= 2;

// Copy is only offered one level below the top — the same rows as before, now read straight off depth.
// Shared by the Configurations list, configure-product, and products tab.
export const canCopyItem = (item: DepthItem): boolean => item.depth === 2;

// Every node id that has children, so a tree can start fully expanded by default. Shared by both
// tree views (configure-product's single-config model and the list-view's HierarchyNode trees).
export const collectExpandableItemIds = <T extends HierarchyItem>(nodes: HierarchyNode<T>[]): string[] => {
  const ids: string[] = [];

  nodes.forEach(node => {
    if (node.children.length > 0) {
      ids.push(node.systemProductConfigurationItemId ?? node.systemProductConfigurationId ?? "");
      ids.push(...collectExpandableItemIds(node.children));
    }
  });

  return ids;
};

// Removed: getLabelsForEntity. It scanned the whole labels array (with a nested reference scan) per
// call, so a list of N rows cost N × |labels| × |references| and re-ran for every row on any label
// change. Use buildLabelIndex once per list and look up by entity id instead.

// Dimensions always read Width x Height. The API returns the parts ordered by the DimensionParts enum
// (Height = 10 before Width = 20), so the join order is set here rather than taken from the response.
const DIMENSION_PART_DISPLAY_ORDER: Shared_DimensionParts[] = [Shared_DimensionParts.Width, Shared_DimensionParts.Height];

// Non-dimension attributes carry a single entry with a null part, which lands after both parts and
// keeps its position under a stable sort.
const getDimensionPartDisplayIndex = (part: Shared_DimensionParts | null): number => {
  const index = part === null ? -1 : DIMENSION_PART_DISPLAY_ORDER.indexOf(part);
  return index === -1 ? DIMENSION_PART_DISPLAY_ORDER.length : index;
};

const byDimensionPartDisplayOrder = (left: Model_AttributeValueGet, right: Model_AttributeValueGet): number => getDimensionPartDisplayIndex(left.part) - getDimensionPartDisplayIndex(right.part);

// Scalar attributes show their resolved valueDescription (already correct for List/Reference types);
// a DimensionType attribute has multiple Value entries (one per part) joined together as Width x Height.
const formatAttributeValue = (attribute: Model_AttributeDetailsGet): string =>
  [...attribute.value]
    .sort(byDimensionPartDisplayOrder)
    .map(v => v.valueDescription || v.value)
    .filter(Boolean)
    .join(" x ");

// The catalog holds one attribute per product type, so the same attribute name can appear several
// times over. productTypeName is null for attributes that apply across all product types.
export const getAttributeDisplayName = (attribute: Model_ConfigurationAttributeGet): string => (attribute.productTypeName ? `${attribute.name} (${attribute.productTypeName})` : attribute.name);

type ProductNameItem = {
  productDescription?: string;
  productNumber?: string;
};

// Shared by the product row and the column-width measurement, so the two can never drift apart.
export const getProductDisplayName = (item: ProductNameItem): string => `${item.productDescription ?? ""} (${item.productNumber ?? ""})`;

export const getConfigurationChipLabels = (configurationId: string | undefined, labelIndex: Map<string, Model_LabelAndReferenceGet[]>): ChipItem[] =>
  (labelIndex.get(configurationId ?? "") ?? []).map((label): ChipItem => ({ text: label.shortname, kind: "LABEL" }));

/**
 * The chips a product row shows, in the user's own View Settings order. Lives here rather than in
 * the row because the list measures the same chips to work out how much width the labels need
 * before it can size the shared name column.
 */
export const getProductChipLabels = (item: Model_ConfigurationsGet_SystemProductConfigurationItem, labelIndex: Map<string, Model_LabelAndReferenceGet[]>, selectedItemIds: string[]): ChipItem[] => {
  const itemLabels = labelIndex.get(item.systemProductConfigurationItemId ?? "") ?? [];

  // Keyed by attributeId (not flattened to strings yet) so the ordered merge below can look up each
  // selected attribute's chip text while walking selectedItemIds. selectedAttributes already omits
  // attributes with no stored value; the blank filter guards the narrower stored-but-empty case.
  const attributeChipTextById = new Map(
    (item.selectedAttributes ?? [])
      .map(attribute => ({ attributeId: attribute.attributeId, description: attribute.description, value: formatAttributeValue(attribute) }))
      .filter(({ value }) => value.trim().length > 0)
      .map(({ attributeId, description, value }): [string, string] => [attributeId, `${description}: ${value}`])
  );

  // Only the Product-labels sentinel expands here, at whatever position the user placed it —
  // Configuration labels are rendered once per configuration by the parent row, not per line item.
  return selectedItemIds.flatMap((itemId): ChipItem[] => {
    if (itemId === VIEW_SETTINGS_PRODUCT_LABELS_ITEM_ID) return itemLabels.map((label): ChipItem => ({ text: label.shortname, kind: "LABEL" }));
    if (itemId === VIEW_SETTINGS_PRODUCT_TYPE_ITEM_ID) return item.productTypeName ? [{ text: item.productTypeName, kind: "PRODUCT_TYPE" }] : [];
    const text = attributeChipTextById.get(itemId);
    return text ? [{ text, kind: "ATTRIBUTE" }] : [];
  });
};

// Depth of the deepest item, counting a root (parentProjectLineItemId === null) as 0. The TreeView
// indents each level, so the shared column sizing has to reserve that indentation.
export const getMaxHierarchyDepth = <T extends HierarchyItem>(items: T[]): number => {
  const getItemId = (item: HierarchyItem): string => item.systemProductConfigurationItemId ?? item.systemProductConfigurationId ?? "";

  const parentById = new Map<string, string | null | undefined>();
  items.forEach(item => parentById.set(getItemId(item), item.parentProjectLineItemId));

  let maxDepth = 0;
  items.forEach(item => {
    // visited guards against cycles and dangling parent ids, same as the tree builders above
    const visited = new Set<string>([getItemId(item)]);
    let depth = 0;
    let parentId = item.parentProjectLineItemId;
    while (parentId !== null && parentId !== undefined && !visited.has(parentId)) {
      visited.add(parentId);
      depth++;
      parentId = parentById.get(parentId);
    }
    maxDepth = Math.max(maxDepth, depth);
  });
  return maxDepth;
};

export const buildLabelIndex = (labels: Model_LabelAndReferenceGet[] = []): Map<string, Model_LabelAndReferenceGet[]> => {
  const index = new Map<string, Model_LabelAndReferenceGet[]>();
  for (const label of labels) {
    for (const ref of label.references) {
      const entityId = ref.entityId ?? "";
      // Read back the just-set/just-existing bucket instead of asserting non-null on the Map lookup
      const bucket = index.get(entityId) ?? [];
      bucket.push(label);
      index.set(entityId, bucket);
    }
  }
  return index;
};
